import { inject, signal } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { MapApiService } from '@app/modules/cop/services/map-api.service';
import { WidgetLocation, LocationPlace } from '../../../models';
import { MapWallDisplayFacade } from '../../../store/map-wall-display.facade';
import { TranslateService } from '@ngx-translate/core';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

export abstract class LocationWidget {
  protected state = inject(MapWallDisplayFacade);
  protected selectedLocationUId = signal<string>(undefined);
  private geocoder: google.maps.Geocoder;
  private mapApi = inject(MapApiService);
  private translate = inject(TranslateService);
  private notify = inject(SystemNotificationsService);

  protected async getCurrentMapPositionAsWidgetLocation(): Promise<WidgetLocation> {
    const {
      center: { lat, lng },
      zoomLevel
    } = this.state.mapPosition();

    const location: WidgetLocation = {
      uId: uuid(),
      name: 'New Place',
      focusPoint: {
        longitude: lng,
        latitude: lat,
        zoomLevel
      },
      timezone: null,
      alpha2CountryCode: null,
      code: ''
    };

    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }

    const place = await this.geocoder
      .geocode({ location: { lat, lng } })
      .then(({ results }) => {
        if (!results.length) {
          return null;
        }

        const place: LocationPlace = {};

        const { address_components = [], formatted_address = null } =
          results.find(({ types }) => types.includes('locality')) ?? results[0] ?? {};

        if (Boolean(formatted_address)) {
          place.displayName = formatted_address;
        }

        address_components?.forEach((c: google.maps.GeocoderAddressComponent) => {
          const type = c?.types[0] ?? null;

          if (type === 'country') {
            place.country = c.long_name;
            place.countryCode = c.short_name;
          } else if (type === 'locality' || type === 'postal_town') {
            place.locality = c.short_name;
          } else if (type === 'administrative_area_level_1') {
            place.area = c.short_name;
          }
        });

        return place;
      })
      .catch(err => {
        console.error(err);
        this.notify.error(this.translate.instant('MAP-DISPLAY.LOCATIONS.ERROR'));
        return null;
      });

    const timezone = await this.mapApi
      .getTimezoneFromLocation(lat, lng)
      .toPromise()
      .then(({ timezoneId }) => timezoneId)
      .catch(err => {
        console.error(err);
        return null;
      });

    if (!Boolean(place) || !Boolean(timezone)) {
      return;
    }

    location.place = place;
    location.name = place.locality;
    location.alpha2CountryCode = place?.countryCode;

    if (Boolean(location.name) && (location.alpha2CountryCode === 'US' || location.alpha2CountryCode === 'CA')) {
      location.name += `, ${place.area}`;
    } else if (!Boolean(location.name)) {
      location.name = place.area ?? this.translate.instant('MAP-DISPLAY.LOCATIONS.NEW');
    }

    location.timezone = timezone;

    return location;
  }
}
